import { useState } from 'react';
import { Avatar, DisclosureRegion, Icon } from '../common';
import { weekPayRow, outstanding } from './pay';
import { replayAnim } from '../../lib/replayAnim';
import type { PayMark } from './usePayMarks';
import type { Shift, Staff } from './types';

interface Props {
  weekDates: string[]; // Fri→Thu
  staff: Staff[];
  shifts: Shift[];
  minimumWage: number;
  marks: PayMark[];
  filterStaffId: string | null;
  // Toggle a helper's week paid/unpaid (paid = current state).
  onToggle: (staffId: string, weekStart: string, paid: boolean) => void;
}

// The money foot of a week-block — what replaced the Wages lens.
//
// Collapsed, it still ANSWERS: hours, wage and what is still owed for that week
// are on the strip itself. That is the point of the design — the reason to look at
// wages is nearly always to know a number, not to change one, and the old Wages tab
// made you leave the roster to find out. Expanding is for ACTING: the per-helper
// cards appear, and marking someone paid happens without ever losing sight of the
// week you are looking at.
//
// Each week's strip opens independently. A quiet week reads as quiet: only helpers
// who actually worked appear, so an empty week says so rather than showing a wall of
// zeroes.
export function WeekMoneyStrip({
  weekDates,
  staff,
  shifts,
  minimumWage,
  marks,
  filterStaffId,
  onToggle,
}: Props) {
  const [open, setOpen] = useState(false);
  const weekStart = weekDates[0]!;
  const isPaid = (id: string) => marks.some((m) => m.staffId === id && m.weekStart === weekStart);
  const rows = staff
    .map((s) => weekPayRow(s, weekDates, shifts, minimumWage, isPaid(s.id)))
    .filter((r) => r.shifts > 0);

  const hours = rows.reduce((a, r) => a + r.hours, 0);
  const wage = rows.reduce((a, r) => a + r.wage, 0);
  const owed = outstanding(rows);

  if (rows.length === 0) {
    return (
      <div className="week-money empty">
        <span className="wm-label">Hours &amp; wages</span>
        <span className="wm-none">No shifts this week</span>
      </div>
    );
  }

  return (
    <div className={'week-money' + (open ? ' open' : '')}>
      <button
        type="button"
        className="wm-head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="wallet" size={13} className="wm-ico" />
        <span className="wm-label">Hours &amp; wages</span>
        <span className="wm-sum mono">
          {hours.toFixed(1)}h · €{wage.toFixed(2)}
        </span>
        {owed === 0 ? (
          <span className="wm-clear">All paid</span>
        ) : (
          <span className="wm-out mono">€{owed.toFixed(2)} outstanding</span>
        )}
        {/* One chevron, rotated when open — the app's own disclosure idiom. */}
        <Icon name="chevron-down" size={13} className="wm-chev" />
      </button>

      {/* THE SHARED DISCLOSURE. This strip is where the app's reveal idiom was
          worked out — body always mounted (a conditionally rendered child cannot
          be animated, it can only appear), height from a 0fr→1fr grid so there
          is no max-height to guess, and a two-helper week opens as smoothly as a
          six-helper one. It was then copied into the Daily command strip, at
          which point there were two of it; the mechanism moved into
          DisclosureRegion and this reads it rather than keeping its own. The
          primitive also marks the closed body `inert`, which does in one place
          what this did per-element with tabIndex={-1} on each card — and covers
          anything else the body might later contain. */}
      <DisclosureRegion open={open} className="wm-reveal">
        {/* .rpay-body.cards is the Wages lens's own card layout — reused
            verbatim, not reinvented. */}
          <div className="rpay-body cards">
            {rows.map((r) => {
            const dim = filterStaffId !== null && r.staff.id !== filterStaffId;
            const first = r.staff.name.split(' ')[0];
            return (
              <button
                type="button"
                key={r.staff.id}
                className={'rpay-card' + (r.paid ? ' paid' : '') + (dim ? ' dim' : '')}
                aria-pressed={r.paid}
                // Closed, the strip has zero height and clips its content — but a
                // clipped button is still TABBABLE. Take it out of the tab order so
                // keyboard users don't fall into an invisible card. (React 18's types
                // predate `inert`, which is what this would otherwise be.)
                title={r.paid ? 'Click to mark unpaid' : `Mark ${first}'s week paid`}
                onClick={(e) => {
                  replayAnim(e.currentTarget);
                  onToggle(r.staff.id, weekStart, r.paid);
                }}
              >
                <div className="rpay-card-head">
                  <Avatar name={r.staff.name} id={r.staff.id} size={26} />
                  <span className="name">{first}</span>
                </div>
                <div className="rpay-card-figs">
                  <div className="big mono">{r.hours.toFixed(1)}h</div>
                  <div className="sub">
                    {r.shifts} shifts · €{r.rate.toFixed(2)}/hr
                  </div>
                  <div className="wage mono">€{r.wage.toFixed(2)}</div>
                </div>
                <span className={'pay-pill' + (r.paid ? ' paid' : '')}>
                  <span className="dot-ind" />
                  {r.paid ? 'Paid' : 'Mark paid'}
                </span>
              </button>
              );
            })}
          </div>
      </DisclosureRegion>
    </div>
  );
}
